import BookmarkIcon from 'components/ReusableComponents/Icons/Bookmark'
import LinkIcon from 'components/ReusableComponents/Icons/Link'
import ConsentRequestDialog from 'components/ReusableComponents/Messaging/UserInfo/ConsentRequestDialog'
import DoNotCallDialog from 'components/ReusableComponents/Messaging/UserInfo/DoNotCallDialog'
import OptInButton from 'components/ReusableComponents/Messaging/UserInfo/OptInButton'
import OptOutButton from 'components/ReusableComponents/Messaging/UserInfo/OptOutButton'
import { getChatRouteFromMessagingType } from 'features/Messaging/messaging-helpers'
import { useInternalAdmin } from 'hooks'
import useCopyText from 'hooks/useCopyText'
import useMessageFollowUp from 'hooks/useMessageFollowUp'
import usePlausible from 'hooks/usePlausible'
import PropTypes from 'prop-types'
import { useState } from 'react'
import { useDispatch } from 'react-redux'

import { resolveThread } from '../../../../actions/messaging'
import ConfirmationDialog from '../../ConfirmationDialog/index'
import OptInIcon from '../../Icons/ResolveIcon'
import RestrictedDateWarningUserInfo from './RestrictedDateWarning'
import styles from './UserInfo.module.scss'

const UserInfo = ({
  name = 'Unknown',
  chatId,
  messagingType,
  resolvable = true,
  restrictedDateName,
  subscriptionStatus,
  isResolving = false,
  hotelId
}) => {
  const [showResolveDialog, setShowResolveDialog] = useState(false)

  const isInternalAdmin = useInternalAdmin()

  const [showFollowUpDialog, setShowFollowUpDialog] = useState(false)

  const dispatch = useDispatch()

  const { saveFollowUp, followUps } = useMessageFollowUp()

  const [followUpNote, setFollowUpNote] = useState('')

  const [showConsentRequestDialog, setShowConsentRequestDialog] = useState(false)
  const [showDoNotCallDialog, setShowDoNotCallDialog] = useState(false)

  const { postEvent, plausibleEvents } = usePlausible()
  const copy = useCopyText()

  function handleResolve() {
    postEvent(plausibleEvents.ResolveChat, {
      chatId
    })
    dispatch(resolveThread(chatId, messagingType))
    setShowResolveDialog(false)
  }

  function handleSaveFollowUp() {
    saveFollowUp(chatId, name, messagingType, followUpNote)
    setShowFollowUpDialog(false)
  }

  const copyFollowUpLink = () => {
    const { protocol, host } = window.location
    copy(
      `${protocol}//${host}/s/${hotelId}${getChatRouteFromMessagingType(
        messagingType
      )}/${chatId}`
    )
  }

  return (
    <div>
      <div className={styles.container}>
        {showConsentRequestDialog && (
          <ConsentRequestDialog
            onClose={() => setShowConsentRequestDialog(false)}
            chatId={chatId}
          />
        )}
        {showDoNotCallDialog && (
          <DoNotCallDialog
            onClose={() => setShowDoNotCallDialog(false)}
            chatId={chatId}
            guestName={name}
          />
        )}
        {showFollowUpDialog && (
          <ConfirmationDialog
            title="Save for follow up"
            positiveMessage="Save"
            negativeMessage="Cancel"
            onConfirm={() => handleSaveFollowUp()}
            onReject={() => setShowFollowUpDialog(false)}
          >
            <textarea
              className={styles.textarea}
              placeholder="Add a note (optional)"
              value={followUpNote}
              onChange={e => setFollowUpNote(e.target.value)}
            ></textarea>
          </ConfirmationDialog>
        )}
        {showResolveDialog && (
          <ConfirmationDialog
            title="Do you want to resolve this conversation?"
            positiveMessage="Resolve conversation"
            negativeMessage="Cancel"
            onReject={() => setShowResolveDialog(false)}
            onConfirm={handleResolve}
          >
            <p>
              Make sure guest is satisfied with the chat before resolving conversations.
            </p>
          </ConfirmationDialog>
        )}
        <div className={styles.userInfoLeft}>
          <div className={styles.userInfoInner}>
            <div className={styles.name}>{name}</div>
          </div>
        </div>
        <div className={styles.controls}>
          {subscriptionStatus === 'DoNotCall' && (
            <OptInButton onClick={() => setShowConsentRequestDialog(true)} />
          )}
          {['Active', 'ConsentGiven', 'WaitingOptIn'].includes(subscriptionStatus) && (
            <OptOutButton onClick={() => setShowDoNotCallDialog(true)} />
          )}
          {resolvable && (
            <button
              onClick={() => setShowResolveDialog(true)}
              className={styles.resolveButton}
              disabled={isResolving}
            >
              <OptInIcon />
              {isResolving ? 'Resolving...' : 'Resolve'}
            </button>
          )}
          {isInternalAdmin && (
            <button
              className={styles.resolveButton}
              onClick={() => setShowFollowUpDialog(true)}
              disabled={followUps[chatId]}
            >
              <BookmarkIcon />
              {followUps[chatId] ? 'Saved' : 'Save'}
            </button>
          )}
          {isInternalAdmin && (
            <button className={styles.resolveButton} onClick={copyFollowUpLink}>
              <LinkIcon />
              {'Copy'}
            </button>
          )}
        </div>
      </div>
      {restrictedDateName && messagingType !== 'service' && (
        <RestrictedDateWarningUserInfo name={restrictedDateName} />
      )}
    </div>
  )
}

UserInfo.propTypes = {
  name: PropTypes.string,
  chatId: PropTypes.string,
  resolvable: PropTypes.bool,
  isResolving: PropTypes.bool
}

export default UserInfo
